<template>
  <div class="app-shell">
    <router-view />
    <CookieConsent />
    <AIDisclosureNotice />
  </div>
</template>

<script>
import CookieConsent from './components/CookieConsent.vue'
import AIDisclosureNotice from './components/AIDisclosureNotice.vue'
import { hasAnalyticsConsent } from './utils/analyticsConsent.js'

export default {
  name: 'App',
  components: {
    CookieConsent,
    AIDisclosureNotice
  },
  data() {
    return {
      stayVisibleSince: null,
      stayAccumulatedMs: 0,
      lastStayEventSentAt: 0,
      stayHeartbeatIntervalId: null
    }
  },
  mounted() {
    this.startStayTracking()
    this.onAnalyticsConsentGranted = () => this.startStayTracking()
    window.addEventListener('analytics-consent-granted', this.onAnalyticsConsentGranted)
  },
  unmounted() {
    this.stopStayTracking()
    if (this.onAnalyticsConsentGranted) {
      window.removeEventListener('analytics-consent-granted', this.onAnalyticsConsentGranted)
    }
  },
  methods: {
    startStayTracking() {
      if (!hasAnalyticsConsent() || this.stayHeartbeatIntervalId) {
        return
      }

      this.stayVisibleSince = Date.now()

      this.onVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
          this.captureVisibleTime()
          this.fireStayDurationEvent()
          return
        }
        this.stayVisibleSince = Date.now()
      }

      this.onPageHide = () => {
        this.captureVisibleTime()
        this.fireStayDurationEvent()
      }

      this.onBeforeUnload = () => {
        this.captureVisibleTime()
        this.fireStayDurationEvent()
      }

      this.stayHeartbeatIntervalId = window.setInterval(() => {
        if (document.visibilityState === 'visible') {
          this.fireStayDurationEvent()
        }
      }, 30000)

      document.addEventListener('visibilitychange', this.onVisibilityChange)
      window.addEventListener('pagehide', this.onPageHide)
      window.addEventListener('beforeunload', this.onBeforeUnload)
    },
    stopStayTracking() {
      this.captureVisibleTime()
      this.fireStayDurationEvent()

      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
      }
      if (this.onPageHide) {
        window.removeEventListener('pagehide', this.onPageHide)
      }
      if (this.onBeforeUnload) {
        window.removeEventListener('beforeunload', this.onBeforeUnload)
      }
      if (this.stayHeartbeatIntervalId) {
        window.clearInterval(this.stayHeartbeatIntervalId)
        this.stayHeartbeatIntervalId = null
      }
    },
    captureVisibleTime() {
      if (!this.stayVisibleSince) {
        return
      }

      this.stayAccumulatedMs += Date.now() - this.stayVisibleSince
      this.stayVisibleSince = null
    },
    fireStayDurationEvent() {
      const now = Date.now()
      if (now - this.lastStayEventSentAt < 1000) {
        return
      }

      const currentVisibleMs =
        document.visibilityState === 'visible' && this.stayVisibleSince
          ? now - this.stayVisibleSince
          : 0
      const durationMs = Math.max(0, this.stayAccumulatedMs + currentVisibleMs)
      if (durationMs < 1000) {
        return
      }

      this.lastStayEventSentAt = now
      this.fireStayTimeEvent(Math.round(durationMs / 1000))
    },
    fireStayTimeEvent(timeSec, attempt = 0) {
      const spPin = window.SP_PIN
      if (spPin && typeof spPin.fireEvent === 'function') {
        spPin.fireEvent('cd89973e-9450-4b6d-be01-b73315268543', {
          timeSec
        })
        return
      }

      if (attempt < 10) {
        setTimeout(() => {
          this.fireStayTimeEvent(timeSec, attempt + 1)
        }, 300)
      }
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--gradient-secondary, linear-gradient(135deg, #0f1023 0%, #1a1b3a 100%));
  min-height: 100vh;
  margin: 0;
  padding: 0;
  position: relative;
  overflow-x: hidden;
  overflow-y: visible;
}

.app-shell {
  position: relative;
  z-index: 1;
  min-height: 100vh;
}

#app::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 15% 15%, rgba(79, 209, 197, 0.03) 0%, transparent 45%),
    radial-gradient(circle at 85% 85%, rgba(161, 127, 224, 0.03) 0%, transparent 45%),
    radial-gradient(circle at 50% 50%, rgba(94, 114, 235, 0.02) 0%, transparent 60%);
  z-index: 0;
  /* TEMPORARILY DISABLED FOR SCROLL TESTING */
  /* animation: gradientAnimation 18s infinite alternate; */
  pointer-events: none;
}

@keyframes gradientAnimation {
  0% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.03);
  }
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
}

html {
  /* scroll-behavior: smooth; */
  overflow-x: hidden;
  overflow-y: auto;
}

body {
  margin: 0;
  padding: 0;
  background: var(--gradient-secondary);
  overflow-x: hidden;
  overflow-y: auto;
}

* {
  box-sizing: border-box;
}

/* Removed fade transition - no page transitions to avoid scroll interference */
</style>
<style src="./assets/styles/tailwind.css"></style>
